import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { CONFIGURABLE_PAGES } from "@/lib/nav";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "gerente", label: "Gerente" },
  { value: "recepcao", label: "Recepção" },
  { value: "professor", label: "Professor" },
];
const ROLE_LABELS = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
const EMPTY = { name: "", email: "", password: "", role: "recepcao", active: true, can_edit_stock: false };

const EDITABLE_ROLES = [
  { value: "gerente", label: "Gerente" },
  { value: "recepcao", label: "Recepção" },
  { value: "professor", label: "Professor" },
];

function PermissionsEditor() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings && perms === null) setPerms(settings.permissions || {});
  }, [settings, perms]);

  if (!perms) return null;

  const toggle = (role, key) =>
    setPerms((p) => {
      const cur = new Set(p[role] || []);
      if (cur.has(key)) cur.delete(key);
      else cur.add(key);
      return { ...p, [role]: [...cur] };
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { permissions: perms });
      toast.success("Permissões atualizadas");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="permissions-editor">
      <div>
        <div className="font-display font-semibold">Permissões por perfil</div>
        <p className="text-sm text-muted-foreground">
          Escolha quais telas cada perfil pode ver. O administrador sempre vê tudo.
        </p>
      </div>
      {EDITABLE_ROLES.map((r) => (
        <div key={r.value} className="flex flex-wrap items-center gap-2">
          <span className="w-24 shrink-0 text-sm font-medium">{r.label}</span>
          {CONFIGURABLE_PAGES.map((p) => {
            const active = (perms[r.value] || []).includes(p.key);
            return (
              <button
                key={p.key}
                data-testid={`perm-${r.value}-${p.key}`}
                onClick={() => toggle(r.value, p.key)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${
                  active
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-500"
                    : "border-border text-muted-foreground hover:bg-accent"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      ))}
      <Button data-testid="perm-save" onClick={save} disabled={saving}>
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Salvar permissões
      </Button>
    </div>
  );
}

export default function Usuarios() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const removeUser = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Usuário excluído");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  const { data: users, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  useEffect(() => {
    if (open) setForm(editing ? { name: editing.name, email: editing.email, password: "", role: editing.role, active: editing.active, can_edit_stock: editing.can_edit_stock || false } : EMPTY);
  }, [open, editing]);

  const save = async () => {
    if (!form.name.trim() || (!editing && (!form.email.trim() || !form.password))) {
      toast.error("Preencha nome, e-mail e senha");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, email: form.email, role: form.role, active: form.active, can_edit_stock: form.can_edit_stock, password: form.password || null });
      } else {
        await api.post("/users", form);
      }
      toast.success(editing ? "Usuário atualizado" : "Usuário criado");
      qc.invalidateQueries();
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Usuários</h1>
          <p className="text-sm text-muted-foreground">Controle de acesso e permissões por perfil</p>
        </div>
        <Button data-testid="new-user-btn" onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="mr-1 h-4 w-4" /> Novo usuário
        </Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {isLoading ? (
            <div className="p-6 space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(users || []).map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{ROLE_LABELS[u.role] || u.role}</Badge>
                      {(u.role === "admin" || u.can_edit_stock) && (
                        <Badge variant="outline" className="ml-1.5 border-emerald-500/40 text-emerald-500">Estoque</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={u.active ? "border-emerald-500/40 text-emerald-500" : "text-muted-foreground"}>
                        {u.active ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-user-${u.id}`}
                          onClick={() => { setEditing(u); setOpen(true); }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500"
                          data-testid={`delete-user-${u.id}`} onClick={() => setDeleteId(u.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="permissions-info">
        <span className="font-semibold text-foreground">Permissões:</span> Recepção registra vendas e movimentações ·
        Gerente também gerencia produtos, planos, formas de pagamento e exclui vendas ·
        Administrador tem acesso total, incluindo usuários e auditoria · Professor apenas visualiza.
      </div>

      <PermissionsEditor />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>E-mail *</Label>
              <Input type="email" data-testid="user-email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "Nova senha (deixe vazio para manter)" : "Senha *"}</Label>
              <Input type="password" data-testid="user-password" value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <Label>Ativo</Label>
              <Switch data-testid="user-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <Label>Pode editar estoque</Label>
              <Switch data-testid="user-can-edit-stock" checked={form.can_edit_stock} onCheckedChange={(v) => setForm((f) => ({ ...f, can_edit_stock: v }))} />
            </div>
            <Button data-testid="user-save" onClick={save} disabled={saving} className="w-full h-11">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              O usuário será excluído (exclusão lógica) e não poderá mais acessar o sistema. O histórico de ações dele é mantido na auditoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={removeUser} data-testid="delete-user-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
